import { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Card,
  CardBody,
  Checkbox,
  CheckboxGroup,
  Input,
  Spinner,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import {
  getExportersWithBrands,
  ExporterWithBrands,
} from 'common/src/api/market_data';
import { DEFAULT_ORIGIN } from 'common/src/utils/definitions';
import {
  saveFavoriteExporters,
  loadFavoriteExporters,
} from 'common/src/api/presettlement-settings';
import useDataStore from 'common/src/stores/useDataStore';
import useFilterStore from 'common/src/stores/useFiltersStore';
import useUserStore from 'common/src/stores/useUserStore';
import { supabase } from 'common/src/supabase';
import { useTranslation } from 'common/src/hooks/useTranslation';
import { Alert } from '../ui';

export const FavoriteExportersSection = () => {
  const { t } = useTranslation('settings');
  const { selectedSpecies } = useDataStore();
  const { selectedSeason } = useFilterStore();
  const { userProfile } = useUserStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporters, setExporters] = useState<ExporterWithBrands[]>([]);
  const [selectedExporters, setSelectedExporters] = useState<string[]>([]);
  // Lo último guardado: Guardar se activa solo si hay cambios.
  const [savedExporters, setSavedExporters] = useState<string[]>([]);
  // Temporada de la que se heredó la lista guardada (null si es propia).
  const [inheritedFrom, setInheritedFrom] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');

  // Load exporters
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const data = await getExportersWithBrands(
          selectedSpecies?.id,
          selectedSeason
        );
        setExporters(data);
      } catch (error) {
        console.error('Error loading exporters:', error);
        toast.error(t('Error al cargar exportadoras'));
      } finally {
        setLoading(false);
      }
    };

    if (selectedSpecies?.id && selectedSeason) {
      loadData();
    }
  }, [selectedSpecies?.id, selectedSeason, t]);

  // Load user's favorite exporters
  useEffect(() => {
    const loadFavorites = async () => {
      if (!userProfile?.exporter_id || !selectedSpecies?.id || !selectedSeason) return;

      try {
        const session = await supabase.auth.getSession();
        const favorites = await loadFavoriteExporters(
          userProfile.exporter_id,
          selectedSeason,
          selectedSpecies.id,
          session.data.session
        );
        setSelectedExporters(favorites.values);
        setSavedExporters(favorites.values);
        setInheritedFrom(favorites.inheritedFrom);
      } catch (error) {
        console.error('Error loading favorite exporters:', error);
      }
    };

    loadFavorites();
  }, [userProfile?.exporter_id, selectedSpecies?.id, selectedSeason]);

  // Separadas por origen (el principal de cada exportadora): así no se
  // mezclan las chilenas con las de EE.UU. o Canadá. Chile primero.
  const exportersByOrigin = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    const groups = new Map<string, string[]>();
    exporters.forEach((e) => {
      if (search && !e.exporter.toLowerCase().includes(search)) return;
      const origin = e.origins[0] || t('Sin origen');
      if (!groups.has(origin)) groups.set(origin, []);
      groups.get(origin)!.push(e.exporter);
    });
    return Array.from(groups.entries()).sort(
      ([a, ea], [b, eb]) =>
        Number(b === DEFAULT_ORIGIN) - Number(a === DEFAULT_ORIGIN) ||
        eb.length - ea.length ||
        a.localeCompare(b)
    );
  }, [exporters, searchText, t]);

  const hasChanges =
    [...selectedExporters].sort().join('|') !== [...savedExporters].sort().join('|');

  // Save favorites to database
  const handleSave = async () => {
    if (!userProfile?.exporter_id || !selectedSpecies?.id || !selectedSeason) return;

    setSaving(true);
    try {
      const session = await supabase.auth.getSession();
      const success = await saveFavoriteExporters(
        userProfile.exporter_id,
        selectedSeason,
        selectedSpecies.id,
        selectedExporters,
        session.data.session
      );

      if (success) {
        setSavedExporters(selectedExporters);
        setInheritedFrom(null);
        toast.success(t('Exportadoras a seguir guardadas exitosamente'));
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Error saving favorite exporters:', error);
      toast.error(t('Error al guardar exportadoras a seguir'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-lg font-bold">{t('Exportadoras a Seguir')}</p>
      </div>

      {inheritedFrom && (
        <Alert showIcon className="mb-4">
          {t(
            "Heredada de la temporada {{season}}. Si la cambias, los cambios quedan solo para {{current}}.",
            { season: inheritedFrom, current: selectedSeason }
          )}
        </Alert>
      )}

      {exporters.length === 0 ? (
        <Card>
          <CardBody>
            <p className="text-center text-default-400">
              {t(
                'No hay datos disponibles para la especie y temporada seleccionadas'
              )}
            </p>
          </CardBody>
        </Card>
      ) : (
        <Card>
          <CardBody>
            <Input
              label={t('Buscar exportadora')}
              value={searchText}
              onValueChange={setSearchText}
              className="mb-4"
              isClearable
              onClear={() => setSearchText('')}
            />
            <CheckboxGroup
              value={selectedExporters}
              onValueChange={setSelectedExporters}
              className="gap-2"
            >
              <div className="space-y-4">
                {exportersByOrigin.map(([origin, originExporters]) => (
                  <div key={origin} className="space-y-2">
                    <p className="text-sm text-default-500">
                      {origin} ({originExporters.length})
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {originExporters.map((exporter) => (
                        <Checkbox key={exporter} value={exporter}>
                          {exporter}
                        </Checkbox>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CheckboxGroup>
            {exportersByOrigin.length === 0 && searchText && (
              <div className="text-center text-default-400 py-4">
                {t('No se encontraron exportadoras')}
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-divider flex items-center justify-between gap-4">
              <p className="text-sm text-default-500">
                {selectedExporters.length > 0 &&
                  `${selectedExporters.length} ${t("exportadoras seleccionadas")}`}
              </p>
              <Button
                color="primary"
                onPress={handleSave}
                isLoading={saving}
                isDisabled={!hasChanges}
              >
                {t("Guardar")}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
};
